import React, { useCallback, useState, useEffect, useRef } from 'react';
import { useTick } from '@pixi/react';
import { Graphics as PixiGraphicsClass } from 'pixi.js'; 
import type { GameState } from './GameComponent'; 

export interface BirdBounds {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface BirdComponentProps {
  x: number;
  initialY: number; 
  width?: number;
  height?: number;
  gravity?: number;
  flapStrength?: number;
  maxYVelocity?: number;
  flapTrigger?: number; 
  gameState?: GameState; 
  onPositionUpdate?: (bounds: BirdBounds) => void;
}

const DEFAULT_BIRD_WIDTH = 34; 
const DEFAULT_BIRD_HEIGHT = 24; 
// Gameplay Tuning V2 ✨
const DEFAULT_GRAVITY = 0.6; 
const DEFAULT_FLAP_STRENGTH = -10; 
const DEFAULT_MAX_Y_VELOCITY = 14; 

const BIRD_BODY_COLOR = 0xFFDE70; 
const BIRD_OUTLINE_COLOR = 0xE0A000; 
const BIRD_BEAK_COLOR = 0xFF8C00;   
const BIRD_WING_COLOR = 0xFFF0A0;  
const BIRD_EYE_COLOR = 0x000000;    

const BirdComponent: React.FC<BirdComponentProps> = ({ 
  x, 
  initialY, 
  width = DEFAULT_BIRD_WIDTH, 
  height = DEFAULT_BIRD_HEIGHT,
  gravity = DEFAULT_GRAVITY,
  flapStrength = DEFAULT_FLAP_STRENGTH,
  maxYVelocity = DEFAULT_MAX_Y_VELOCITY,
  flapTrigger = 0,
  gameState = 'menu',
  onPositionUpdate, 
}) => {
  const [currentY, setCurrentY] = useState(initialY);
  const [velocityY, setVelocityY] = useState(0);
  const prevFlapTriggerRef = useRef(flapTrigger);
  const [flapAnimFrame, setFlapAnimFrame] = useState(0);
  const flapAnimTimerRef = useRef<number | null>(null); 

  useEffect(() => {
    if (onPositionUpdate) {
      onPositionUpdate({
        x: x - width / 2, 
        y: currentY - height / 2,
        width,
        height,
      });
    }
  }, [x, currentY, width, height, onPositionUpdate]);

  const internalFlap = useCallback(() => {
    setVelocityY(flapStrength);
    setFlapAnimFrame(1); 
    if (flapAnimTimerRef.current !== null) { 
      clearTimeout(flapAnimTimerRef.current);
    }
    flapAnimTimerRef.current = window.setTimeout(() => { 
      setFlapAnimFrame(0); 
    }, 120); 
  }, [flapStrength]);

  useEffect(() => {
    if (flapTrigger !== prevFlapTriggerRef.current) {
      if (gameState === 'playing') {
        internalFlap();
      }
      prevFlapTriggerRef.current = flapTrigger;
    }
  }, [flapTrigger, gameState, internalFlap]);

  useEffect(() => {
    if (gameState === 'menu' || gameState === 'gameOver') {
      setCurrentY(initialY);
      setVelocityY(0);
      setFlapAnimFrame(0);
      if (flapAnimTimerRef.current !== null) { 
        clearTimeout(flapAnimTimerRef.current);
        flapAnimTimerRef.current = null; 
      }
    }
  }, [gameState, initialY]);
  
  useTick((ticker) => {
    if (gameState === 'playing') {
      const delta = ticker.deltaTime;
      
      setVelocityY(prevVy => {
        let newVy = prevVy + gravity * delta;
        if (newVy > maxYVelocity) newVy = maxYVelocity;
        return newVy;
      });
      
      setCurrentY(prevY => prevY + velocityY * delta);
    }
  });

  const drawBird = useCallback((g: PixiGraphicsClass) => {
    g.clear();

    const bodyRadius = height / 2.8; 

    // 1. Body ✨ - Updated to PixiJS v8 API
    g.stroke({width: 2, color: BIRD_OUTLINE_COLOR, alpha: 1})
     .fill({color: BIRD_BODY_COLOR})
     .roundRect(0, height * 0.1, width * 0.9, height * 0.8, bodyRadius); 

    // 2. Wing (Drawn AFTER body so it's visible) ✨ - Updated to PixiJS v8 API
    const wingWidth = width * 0.5; 
    const wingHeight = height * 0.6;
    const wingXFromBodyOrigin = width * 0.25; 
    let wingYFromBodyOrigin = height * 0.3;   

    if (flapAnimFrame === 1) { 
      wingYFromBodyOrigin = height * 0.15; 
    }
    g.stroke({width: 1.5, color: BIRD_OUTLINE_COLOR, alpha: 0.9})
     .fill({color: BIRD_WING_COLOR})
     .ellipse(wingXFromBodyOrigin + wingWidth / 2, wingYFromBodyOrigin + wingHeight / 2, wingWidth / 2, wingHeight / 2.2);

    // 3. Beak ✨ - Updated to PixiJS v8 API
    g.stroke({width: 1, color: BIRD_OUTLINE_COLOR, alpha: 0.8})
     .fill(BIRD_BEAK_COLOR) // Can pass color directly to fill if alpha is 1
     .poly([
       width * 0.85, height * 0.4, 
       width * 1.10, height * 0.5, 
       width * 0.85, height * 0.6,
       width * 0.85, height * 0.4 // Close the polygon for fill
     ]);

    // 4. Eye ✨ - Updated to PixiJS v8 API
    g.circle(width * 0.65, height * 0.35, height * 0.1)
     .fill(BIRD_EYE_COLOR); // No stroke for eye
    
    g.pivot.set(width / 2, height / 2); 
    if (velocityY < -1) g.angle = -20; 
    else if (velocityY > 1.5) g.angle = Math.min(45, velocityY * 5); 
    else g.angle = 0; 

  }, [width, height, velocityY, flapAnimFrame]); 

  return (
    <pixiGraphics
      draw={drawBird}
      x={x} 
      y={currentY} 
    />
  );
};

export default BirdComponent;
// Rewrite Count: 12